import type { MouseEvent } from 'react';
import { MapPin, Video } from 'lucide-react';

import { cn } from '@/lib/cn';
import { parseLocationLink } from '@/lib/links';

interface Props {
  location: string;
  className?: string;
  /** Compact row for agenda lists; default matches the event sheet. */
  compact?: boolean;
}

/**
 * Calendar location: plain place name, or a clickable Zoom/Teams/Meet link.
 */
export function EventLocation({ location, className, compact = false }: Props) {
  const link = parseLocationLink(location);
  const iconSize = compact ? 12 : 14;

  if (!link) {
    return (
      <span className={cn('inline-flex max-w-full items-center gap-1 truncate', className)}>
        <MapPin size={iconSize} strokeWidth={1.5} className="shrink-0" aria-hidden />
        <span className="truncate">{location}</span>
      </span>
    );
  }

  const isMeeting =
    link.label.startsWith('Join ') ||
    /^zoommtg:|^msteams:/i.test(link.href) ||
    /zoom\.us|teams\.microsoft|teams\.live|meet\.google/i.test(link.href);

  const stopRowOpen = (e: MouseEvent) => {
    e.stopPropagation();
  };

  return (
    <a
      href={link.href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={stopRowOpen}
      className={cn(
        'inline-flex max-w-full items-center gap-1 truncate outline-none focus-visible:ring-2 focus-visible:ring-ring',
        'text-accent hover:underline',
        className,
      )}
    >
      {isMeeting ? (
        <Video size={iconSize} strokeWidth={1.5} className="shrink-0" aria-hidden />
      ) : (
        <MapPin size={iconSize} strokeWidth={1.5} className="shrink-0" aria-hidden />
      )}
      <span className="truncate">{link.label}</span>
    </a>
  );
}
